import { useEffect, useRef, useState } from 'react';
import { cloneDeep } from 'lodash-es';
import { useApolloClient } from '@apollo/client';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useAutofill } from '@apps/card/contexts/AutofillContext';
import { resetCardTemplateDefaults } from '@apps/card/routes/Dashboard/components/DesignsGallery/utils';
import { useCreateStationeryDraftMutation } from '@apps/card/routes/Dashboard/components/ProductDetailsPage/ProductDetailsPage.graphql.generated';
import { useUpdateStationeryDraftMutation } from '../../CardCustomizer.graphql.generated';
import { useCustomizationContext } from '../../useCardCustomization';
import { useDraftSaverFlush } from '../../useDraftSaver';
import type { DraftJson } from '../../DraftJson.types';
import type { StartBasicDigitalDraftTemplateQuery, StartBasicDigitalDraftTemplateQueryVariables } from './useStartBasicDigitalDraft.graphql.generated';
import { StartBasicDigitalDraftTemplateDocument } from './useStartBasicDigitalDraft.graphql.generated';

type UseStartBasicDigitalDraftArgs = Readonly<{
  draftId: string;
}>;

type BasicDigitalProduct = Readonly<{ priceConfigId: string }>;

type StartOptions = Readonly<{
  /** Called once the new draft exists and before the page navigates to it. */
  onStarted?: () => void;
}>;

/**
 * Save this draft, start a new Basic Digital V2 draft from the template's original design, and open
 * it in the digital customizer.
 *
 * Basic Digital is never reached by switching the current draft's product: the free product sells
 * one silhouette and one wallpaper colour, so the draft's design would have to be put back as well,
 * and a paper draft's config is not a digital one at all. A new draft from the template's own
 * design is what the PDP's Customize Now creates, and leaves this draft as it was in Drafts.
 *
 * Throws if any step failed. The current draft is saved before anything else, so a failure past
 * that point leaves it saved and unchanged.
 */
export const useStartBasicDigitalDraft = ({ draftId }: UseStartBasicDigitalDraftArgs) => {
  const [draftConfig] = useCustomizationContext(d => d.config);
  const { goToDigitalCustomizeDraft } = useCardsRouterContext();
  const { eventInfo } = useEventInfo();
  const flushPendingSave = useDraftSaverFlush();
  const client = useApolloClient();
  // `autofill` silently does nothing until the event's location, privacy and password have loaded,
  // and a draft created meanwhile keeps the template's placeholder couple for good. The PDP holds
  // its page on this; here the start waits instead.
  const { autofill, loading: isAutofillLoading } = useAutofill();

  const [saveDraft] = useUpdateStationeryDraftMutation();
  const [createStationeryDraft] = useCreateStationeryDraftMutation({
    // Same lists the PDP refreshes on a new draft, so the Drafts tab and the burger menu show it
    // without a reload. See `useCustomizePremiumDigitalCard`.
    refetchQueries: () => ['GetDigitalAndPaperDraftsByEventId', 'GetExistingEventStationeryDraftsWithTemplate', 'SmartRoutingData'],
  });

  const [isStarting, setIsStarting] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const startBasicDigitalDraft = useEventCallback(async (product: BasicDigitalProduct, options?: StartOptions): Promise<boolean> => {
    const eventId = eventInfo?.eventId;
    if (isStarting || isAutofillLoading || !eventId) return false;

    setIsStarting(true);
    try {
      // Settle the throttled saver, then write what is on screen, as a product switch does: the
      // user is told this draft is saved, so the last edits must have landed before we leave it.
      await flushPendingSave();
      await saveDraft({ variables: { payload: { id: draftId, eventId, cardJSON: draftConfig } } });

      const { data } = await client.query<StartBasicDigitalDraftTemplateQuery, StartBasicDigitalDraftTemplateQueryVariables>({
        query: StartBasicDigitalDraftTemplateDocument,
        variables: { draftId },
      });
      const template = data.stationeryDraftById?.stationeryTemplate;
      if (!template) throw new Error('Draft has no template to start Basic Digital from');

      // Prepared as the PDP prepares it (`usePdpData`): the default back, envelope and cut, then the
      // event's names, date and location filled in -- the raw template carries placeholder couples.
      const draftTemplate = { id: template.id, themeJSON: cloneDeep(template.themeJSON) as DraftJson };
      resetCardTemplateDefaults(draftTemplate);
      const defaultCut = draftTemplate.themeJSON.card.front.dimensions?.defaultCut;
      if (defaultCut) {
        draftTemplate.themeJSON.customizations.shape = defaultCut;
      }
      autofill(draftTemplate);

      const response = await createStationeryDraft({
        variables: {
          payload: {
            eventId,
            cardJSON: draftTemplate.themeJSON,
            stationeryTemplateId: template.id,
            format: StationeryDraftFormat.digital,
            // The free product's own price config is what makes the new draft Basic Digital.
            priceConfigId: product.priceConfigId,
          },
        },
      });
      const newDraftId = response.data?.createStationeryDraft.id;
      if (!newDraftId) throw new Error('createStationeryDraft returned no draft');

      options?.onStarted?.();
      goToDigitalCustomizeDraft(newDraftId);
      return true;
    } finally {
      if (isMountedRef.current) {
        setIsStarting(false);
      }
    }
  });

  return { startBasicDigitalDraft, isStarting, isReady: !isAutofillLoading };
};
